import type { IconType } from '@main/core-ui';
import { FlexColumnCenterVertical, Icon } from '@main/core-ui';
import { SanitizeHtml } from '@main/pc-core-components';
import type { ID } from '@main/schema-utils';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { selectDataSubjects } from '../../containers/ActiveDataSubject/selectors';
import { StyledReactSelect } from '../SelectPartition/wrappers';
import { selectDataSubjectTypeMessages } from './messages';

export interface SelectDataSubjectTypeOption {
  /** Subject id */
  id: ID<'subject'>;
  /** Subject type */
  type: string;
}

/**
 * Type for the options of the SelectDataSubjectType component.
 */
interface SelectDataSubjectTypeInternalOption {
  /** Subject id */
  id: ID<'subject'>;
  /** Subject type */
  value: string;
  /** Option label */
  label: string;
  /** Option logo */
  logo: React.JSX.Element;
  /** Option display variant */
  variant: 'light';
}

interface SelectDataSubjectTypeProps {
  /** default data subject type */
  value: SelectDataSubjectTypeOption;
  /** Callback to handle the change of a data subject type */
  onChange: (type: SelectDataSubjectTypeOption) => void;
}

/**
 * Component to Select DataSubjectType.
 */
export const SelectDataSubjectType: React.FC<
  React.PropsWithChildren<SelectDataSubjectTypeProps>
> = ({ value, onChange }) => {
  const { formatMessage } = useIntl();
  const subjects = useSelector(selectDataSubjects);
  const options: SelectDataSubjectTypeInternalOption[] = subjects.map(
    (subject) => ({
      id: subject.id,
      value: subject.type,
      label: formatMessage(subject.title),
      logo: <Icon type={subject.icon as IconType} />,
      variant: 'light',
    }),
  );
  const defaultOption = options.find((option) => option.value === value.type)!;

  return (
    <>
      {options.length > 1 && (
        <FlexColumnCenterVertical style={{ flex: '1' }}>
          <p>
            <SanitizeHtml
              html={formatMessage(
                selectDataSubjectTypeMessages.selectDataSubjectType,
              )}
            />
          </p>
          <StyledReactSelect
            isMulti={false}
            variant="light"
            id="select-dataSubject-type"
            options={options}
            value={defaultOption}
            isClearable={false}
            getOptionLabel={(option) => option.label}
            onChange={(option) => {
              if (option) {
                // Cast option since we can't type StyledReactSelect
                const selectedOption =
                  option as SelectDataSubjectTypeInternalOption;
                onChange({
                  type: selectedOption.value,
                  id: selectedOption.id,
                });
              }
            }}
          />
        </FlexColumnCenterVertical>
      )}
    </>
  );
};
